import { useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { EMBROIDERY_ICON_GROUPS_QUERY } from '../graphql/queries'

import { QueryStatus } from './types'

export type EmbroideryIconGroup = {
  name: string
  displayName: string
  sortOrder: number | undefined
  maxIconsPerRowMobile: number | undefined
  disabledInitialsText: string | undefined
}

type UseFetchEmbroideryIconGroupsOutput = {
  embroideryIconGroups: EmbroideryIconGroup[]
  embroideryIconGroupsStatus: QueryStatus
}

type EmbroideryIconGroupsData = {
  embroideryIconGroups: EmbroideryIconGroup[]
}

export function useFetchEmbroideryIconGroups(
  isPreview?: boolean
): UseFetchEmbroideryIconGroupsOutput {
  const {
    error: embroideryIconGroupsError,
    data: embroideryIconGroupsData,
    loading: embroideryIconGroupsLoading,
    previousData: previousEmbroideryIconGroupsData,
    called: embroideryIconGroupsCalled,
  } = useQuery<EmbroideryIconGroupsData>(EMBROIDERY_ICON_GROUPS_QUERY, {
    variables: { isPreview },
  })

  const embroideryIconGroupsUnsorted = useMemo(() => {
    return (
      (embroideryIconGroupsLoading
        ? previousEmbroideryIconGroupsData?.embroideryIconGroups
        : embroideryIconGroupsData?.embroideryIconGroups) ?? []
    )
  }, [embroideryIconGroupsLoading, previousEmbroideryIconGroupsData, embroideryIconGroupsData])

  const embroideryIconGroupsStatus = useMemo<QueryStatus>(() => {
    if (embroideryIconGroupsLoading) return 'pending'
    if (embroideryIconGroupsError) return 'rejected'
    if (embroideryIconGroupsCalled || embroideryIconGroupsUnsorted) return 'resolved'
    return 'idle'
  }, [
    embroideryIconGroupsLoading,
    embroideryIconGroupsError,
    embroideryIconGroupsCalled,
    embroideryIconGroupsUnsorted,
  ])

  const embroideryIconGroups = [...embroideryIconGroupsUnsorted].sort((a, b) => {
    if (a.sortOrder === b.sortOrder) {
      return 0
    } else if (a.sortOrder == undefined || b.sortOrder == undefined) {
      return a.sortOrder == undefined ? 1 : -1
    } else {
      return a.sortOrder < b.sortOrder ? -1 : 1
    }
  })

  return {
    embroideryIconGroups,
    embroideryIconGroupsStatus,
  }
}
